/**
 * Composition root. The only file that knows which implementations are real.
 *
 * Both seams are wired here and nowhere else:
 *
 *   - `createAuthClient()` — Cognito when the pool is configured, a local fake
 *     otherwise.
 *   - `createApiClient()`  — the generated-client adapter when
 *     `VITE_DATA_SOURCE=live`, the in-memory fake otherwise.
 *
 * The two reference each other, in one direction each: the api client attaches
 * the auth client's ID token per request, and the auth client resolves an org's
 * display name through the api client. Both references are thunks, so the
 * construction order (auth first) never captures a missing dependency. No
 * screen, store, query or component references a transport.
 */

import { VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query'
import { createPinia } from 'pinia'
import { createApp } from 'vue'

import App from './App.vue'
import { ApiError, createApiClient, provideApiClient, type ApiClient } from './lib/api'
import { createAuthClient, provideAuthClient } from './lib/auth'
import { installGuards } from './router/guards'
import { createAppRouter } from './router'
import { useSessionStore } from './stores/session'

// The Ink Clinical faces, self-hosted (no CDN — a clinical product does not
// leak page views to a font host, and the CSP has no external origins). The
// tokens name these three; without the imports the browser silently falls back
// to Georgia, system-ui and ui-monospace, and the theme's typographic identity
// exists nowhere but the token file.
//
// Weights: serif 600/700 for headings (--heading-weight is 600), sans
// 400/600/700 for body, emphasis and table headers — without the real 700 the
// browser synthesizes a faux bold, which is exactly the kind of quiet
// degradation a token system exists to prevent. Geist Mono carries numerals
// only, so it ships 400/600 and no more.
//
// `latin-` prefixed, not the bare weight file. The bare import pulls every
// subset the family ships — Cyrillic, Greek, Vietnamese — none of which a
// US-only product renders. Source Sans 3 alone was shipping eighteen extra
// font files this way.
import '@fontsource/newsreader/latin-600.css'
import '@fontsource/newsreader/latin-700.css'
import '@fontsource/instrument-sans/latin-400.css'
import '@fontsource/instrument-sans/latin-600.css'
import '@fontsource/instrument-sans/latin-700.css'
import '@fontsource/geist-mono/latin-400.css'
import '@fontsource/geist-mono/latin-600.css'

import './styles/base.css'

const app = createApp(App)

app.use(createPinia())

// Assigned a few lines down; the thunks close over it.
let api: ApiClient | null = null

// The store is attached before anything can read it, so no guard or component
// ever sees a session store without a client.
const auth = createAuthClient({
  getOrgName: async (orgId) => {
    if (!api) return null
    const view = await api.getOrg(orgId)
    return view.name
  }
})
const session = useSessionStore()
session.attach(auth)

// The implementations need to know who is signed in — to greet by name, to
// address the owner-only routes, and to compose Home. Getters rather than values
// so the client does not capture a stale session on the role switch or a token
// refresh.
api = createApiClient(
  {
    currentOrgId: () => session.session?.orgId ?? null,
    currentRole: () => session.role,
    currentDisplayName: () => session.session?.displayName ?? 'there'
  },
  { getToken: () => auth.getIdToken(), isStaff: () => auth.isStaffSession() }
)
provideApiClient(app, api)
provideAuthClient(app, auth)

const queryOptions: VueQueryPluginOptions = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        // Thirty seconds. Long enough that navigating away and back does not
        // re-fetch, short enough that a bed count is not stale in a way the
        // freshness stamp cannot explain.
        staleTime: 30_000,
        // Retrying a 4xx just delays the error. Retrying a 503 once is worth it.
        retry: (attempts, error) => error instanceof ApiError && error.detail.retryable && attempts < 1,
        // Off deliberately. A clinician who alt-tabs to a phone call and back does
        // not need every panel to flicker, and DESIGN.md's freshness stamps are the
        // honest way to communicate age.
        refetchOnWindowFocus: false
      },
      mutations: {
        // Mutations are never retried automatically. Every mutating endpoint takes
        // an idempotency key (spec section 6), but a silent retry would still
        // decide a referral twice from the user's point of view.
        retry: 0
      }
    }
  }
}
app.use(VueQueryPlugin, queryOptions)

const router = createAppRouter()
installGuards(router, session)
app.use(router)

// Restore the session and apply the theme before the first paint, so a dark-theme
// user never sees a light flash and the first guard decision is made against a
// known session rather than `unknown`.
//
// A function rather than top-level await: TLA constrains the build target, and
// that is a large concession for two statements.
async function start (): Promise<void> {
  await session.bootstrap(document.documentElement)
  await router.isReady()
  app.mount('#app')
}

void start()
